import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  input,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Params,
  Router,
} from '@angular/router';
import { PageTabRoute } from './router-tabs.model';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'fz-router-tabs',
  templateUrl: './router-tabs.component.html',
  styleUrls: ['./router-tabs.component.scss'],
})
export class RouterTabsComponent implements OnInit, AfterViewInit {
  readonly #route = inject(ActivatedRoute);
  readonly #destroyRef = inject(DestroyRef);

  @Input()
  disablePagination: boolean;

  @Input()
  links: PageTabRoute[];

  @Input()
  type: 'path' | 'query' = 'path';

  @Input()
  disableGutters = false;

  @Input()
  activeLinkIndex = -1;

  preserveQueryParams = input<boolean>(false);

  @ViewChild(MatTabGroup)
  group: MatTabGroup;

  queryParams = signal<Record<string, Params>>({});

  constructor(private router: Router) {}

  ngOnInit(): void {
    if (this.activeLinkIndex === -1) {
      this.updateActiveLinkIndex();
    }

    if (this.preserveQueryParams()) {
      this.#route.queryParams
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((params) => {
          this.queryParams.update((prev) => ({
            ...prev,
            [this.links[this.activeLinkIndex].path]: {
              ...prev[this.links[this.activeLinkIndex].path],
              ...params,
            },
          }));
        });
    }
  }

  ngAfterViewInit() {
    if (this.activeLinkIndex !== -1) {
      const label = this.group._elementRef.nativeElement.querySelector(
        '.mat-mdc-tab-labels',
      );
      const activeLabel = label.children[this.activeLinkIndex] as HTMLElement;
      activeLabel.scrollIntoView({
        block: 'center',
        inline: 'center',
      });
    }
  }

  private updateActiveLinkIndex() {
    if (this.type === 'path') {
      this.activeLinkIndex = this.links.indexOf(
        this.links.find((tab) => {
          if (tab.pathMatch === 'full') {
            return tab.path === this.router.url;
          }

          return this.router.url.includes(tab.path);
        }) as PageTabRoute,
      );
    }
  }

  swipe(index: number) {
    if (this.type === 'path') {
      const link = this.links[index];
      this.router.navigate([link.path]);
    } else {
      const link = this.links[index];
      this.router.navigate([link.path], {
        queryParams: {
          view: link.view,
          ...(this.preserveQueryParams() ? this.queryParams()[link.path] : {}),
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  selectedTabChange(event: MatTabChangeEvent) {
    this.group.realignInkBar();
  }
}
